import { Bolt, SmartToy } from '@mui/icons-material';
import {
    Avatar,
    alpha,
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    Paper,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { EChatAgentMessageAuthor, type IChatAgentChat, type IChatAgentMessage } from '@sharedInterfaces/ChatAgent';
import type { AppState } from '@store/store';
import { FileChartColumnIncreasing, GraduationCap, Send, Sparkles, Target, Users } from 'lucide-react';
import React from 'react';
import { useSelector } from 'react-redux';
import MobileChatPanel from './MobileChatPanel';
import type { Shortcut } from './types';

const getShortcutIcon = (iconName?: string) => {
    const iconSize = 16;
    switch (iconName) {
        case 'target':
            return <Target size={iconSize} />;
        case 'sparkles':
            return <Sparkles size={iconSize} />;
        case 'graduation-cap':
            return <GraduationCap size={iconSize} />;
        case 'users':
            return <Users size={iconSize} />;
        case 'file-chart-column-increasing':
            return <FileChartColumnIncreasing size={iconSize} />;
        default:
            return <Target size={iconSize} />;
    }
};

interface ChatPanelProps {
    messages: IChatAgentMessage[];
    input: string;
    setInput: (input: string) => void;
    sendMessage: () => void;
    handleKeyPress: (e: React.KeyboardEvent) => void;
    isTyping: boolean;
    chat: IChatAgentChat | null;
    scrollRef: React.RefObject<HTMLDivElement>;
    inputRef: React.RefObject<HTMLInputElement>;
    renderFormattedText: (text: string, isUserMessage?: boolean) => React.ReactNode;
    langStrings: any;
    shortcuts: Shortcut[];
    highlightedShortcut: Shortcut;
    handleShortcutAction: (action: string) => void;
    isMobile?: boolean;
    isAgentAction?: boolean;
}

export const ChatPanel: React.FC<ChatPanelProps> = (props) => {
    const {
        messages,
        input,
        setInput,
        sendMessage,
        handleKeyPress,
        isTyping,
        chat,
        scrollRef,
        inputRef,
        renderFormattedText,
        langStrings,
        shortcuts,
        highlightedShortcut,
        handleShortcutAction,
        isMobile,
        isAgentAction = false,
    } = props;

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const isMobileView = isMobile !== undefined ? isMobile : isSmallScreen;
    const employee = useSelector((state: AppState) => state.employee);

    if (isMobileView) return <MobileChatPanel {...props} />;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'visible' }}>
            <Box
                ref={scrollRef}
                sx={{
                    flexGrow: 1,
                    overflowY: messages.length > 0 ? 'auto' : 'hidden',
                    p: 2,
                    pl: 0,
                    mt: 1,
                    height: 'calc(100% - 120px)',
                }}
            >
                {messages.length === 0 ? (
                    <Box sx={{ textAlign: 'center', p: 4 }}>
                        <Typography color="textSecondary">
                            {langStrings.chatEmptyText || 'Stellen Sie eine Frage, um zu beginnen.'}
                        </Typography>
                    </Box>
                ) : (
                    messages.map((msg, idx) => (
                        <Box
                            key={idx}
                            sx={{
                                display: 'flex',
                                flexDirection: msg.role === EChatAgentMessageAuthor.USER ? 'row-reverse' : 'row',
                                mb: 2.5,
                                maxWidth: '100%',
                            }}
                        >
                            <Avatar
                                sx={{
                                    width: 46,
                                    height: 46,
                                    ml: msg.role === EChatAgentMessageAuthor.USER ? 1 : 0,
                                    mr: msg.role === EChatAgentMessageAuthor.USER ? 1 : 1,
                                    bgcolor:
                                        msg.role === EChatAgentMessageAuthor.USER
                                            ? theme.palette.primary.main
                                            : alpha(theme.palette.background.paper, 0.7),
                                    color:
                                        msg.role === EChatAgentMessageAuthor.USER
                                            ? theme.palette.primary.contrastText
                                            : theme.palette.primary.main,
                                }}
                            >
                                {msg.role === EChatAgentMessageAuthor.USER ? (
                                    employee?.firstName?.charAt(0) || 'U'
                                ) : (
                                    <SmartToy sx={{ color: theme.palette.primary.main }} />
                                )}
                            </Avatar>
                            <Paper
                                elevation={1}
                                sx={{
                                    p: { xs: 1.5, md: 2 },
                                    maxWidth: '90%',
                                    bgcolor:
                                        msg.role === EChatAgentMessageAuthor.USER
                                            ? alpha(theme.palette.primary.main, 0.8)
                                            : theme.palette.background.paper,
                                    color:
                                        msg.role === EChatAgentMessageAuthor.USER
                                            ? theme.palette.primary.contrastText
                                            : theme.palette.text.primary,
                                    borderRadius: '6px',
                                    borderTopLeftRadius: msg.role !== EChatAgentMessageAuthor.USER ? 0 : '6px',
                                    borderTopRightRadius: msg.role === EChatAgentMessageAuthor.USER ? 0 : '6px',
                                }}
                            >
                                {renderFormattedText(msg.text, msg.role === EChatAgentMessageAuthor.USER)}
                            </Paper>
                        </Box>
                    ))
                )}

                {isTyping && (
                    <Box sx={{ display: 'flex', mb: 2.5 }}>
                        <Avatar
                            sx={{ width: 46, height: 46, mx: 1, bgcolor: alpha(theme.palette.background.paper, 0.7) }}
                        >
                            <SmartToy sx={{ color: theme.palette.primary.main }} />
                        </Avatar>
                        <Paper
                            elevation={2}
                            sx={{
                                p: 2,
                                maxWidth: '100%',
                                bgcolor: theme.palette.background.paper,
                                borderRadius: '6px',
                                borderTopLeftRadius: 0,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <CircularProgress size={16} sx={{ mr: 1 }} />
                                <Typography variant="body1" sx={{ fontSize: '1rem' }} color="text.primary">
                                    {'Schreibt...'}
                                </Typography>
                            </Box>
                        </Paper>
                    </Box>
                )}
            </Box>

            <Box
                sx={{ display: 'block', alignItems: 'flex-end', justifyContent: 'center', pt: 1, pr: 3, pb: 1, pl: 0 }}
            >
                <TextField
                    inputRef={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyPress}
                    onFocus={() => {
                        if (inputRef.current) {
                            inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    }}
                    multiline
                    rows={5}
                    maxRows={5}
                    fullWidth
                    placeholder={langStrings.inputPlaceholder || 'Was kann ich heute für dich tun?'}
                    variant="outlined"
                    disabled={false}
                    sx={{
                        mr: 0,
                        width: { xs: '100%' },
                        mx: 'auto',
                        '& .MuiOutlinedInput-root': {
                            bgcolor: alpha(theme.palette.background.paper, 0.95),
                            p: { xs: 1.5, md: 2 },
                        },
                    }}
                    InputProps={{
                        startAdornment: isAgentAction ? (
                            <InputAdornment position="start">
                                <Bolt color="primary" />
                            </InputAdornment>
                        ) : null,
                        endAdornment: (
                            <IconButton
                                onClick={sendMessage}
                                disabled={!input.trim()}
                                sx={{
                                    color: theme.palette.primary.main,
                                    '&.Mui-disabled': { color: alpha(theme.palette.primary.main, 0.3) },
                                }}
                                aria-label={langStrings.sendMessage}
                            >
                                <Send size={20} />
                            </IconButton>
                        ),
                    }}
                />

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        overflowX: 'auto',
                        overflowY: 'visible',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.5,
                        mt: 2,
                        mb: 0,
                        px: 0,
                        pb: 0,
                        maxWidth: '100%',
                        width: { xs: '100%' },
                        minHeight: 40,
                        position: 'relative',
                        zIndex: 10,
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                    }}
                >
                    {shortcuts.map((shortcut, index) => (
                        <Button
                            key={index}
                            variant="animatedBorder"
                            onClick={() => handleShortcutAction(shortcut.action)}
                            size="medium"
                            startIcon={getShortcutIcon(shortcut.icon)}
                            onMouseMove={(e: React.MouseEvent<HTMLButtonElement>) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                const x = ((e.clientX - rect.left) / rect.width) * 100;
                                const y = ((e.clientY - rect.top) / rect.height) * 100;
                                e.currentTarget.style.setProperty('--mouse-x', `${x}%`);
                                e.currentTarget.style.setProperty('--mouse-y', `${y}%`);
                            }}
                            sx={{ flexShrink: 0 }}
                        >
                            {shortcut.text}
                        </Button>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

export default ChatPanel;
